import React, { useState } from 'react';
import { apiService } from '../../../services/api';
import { AdicionarEstoqueData } from '../../../types/estoque';
import { modalUtils } from '../../../utils/modalUtils';
import { NumericFormat } from 'react-number-format';

interface AdicionarEstoqueModalProps {
  produtoId: number | null;
  produtoNome: string;
  estoqueAtual: number;
  unidadeMedida?: string;
  onEstoqueAdicionado: () => void;
}

const motivosEstoque = [
  'Compra fornecedor',
  'Ajuste de inventário',
  'Devolução de empenho',
  'Transferência entre locais',
  'Entrada manual'
];

export const AdicionarEstoqueModal: React.FC<AdicionarEstoqueModalProps> = ({
  produtoId,
  produtoNome,
  estoqueAtual,
  unidadeMedida,
  onEstoqueAdicionado
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<AdicionarEstoqueData>({
    quantidade: 0,
    motivo: '',
    observacoes: ''
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!produtoId) {
      alert('Erro: Produto não identificado');
      return;
    }

    // Validações
    if (formData.quantidade <= 0) {
      alert('A quantidade deve ser maior que zero');
      return;
    }

    if (!formData.motivo.trim()) {
      alert('O motivo é obrigatório');
      return;
    }

    try {
      setIsSubmitting(true);

      await apiService.adicionarEstoqueProduto(produtoId, formData);

      // Limpar formulário
      setFormData({
        quantidade: 0,
        motivo: '',
        observacoes: ''
      });

      // Fechar modal
      modalUtils.fecharModal('modal_adicionar_estoque');

      // Callback para atualizar a listagem
      onEstoqueAdicionado();

      // Mostrar modal de sucesso
      modalUtils.abrirModal('success_estoque_modal');

    } catch (error) {
      console.error('Erro ao adicionar estoque:', error);
      alert('Erro ao adicionar estoque. Por favor, tente novamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      quantidade: 0,
      motivo: '',
      observacoes: ''
    });
    modalUtils.fecharModal('modal_adicionar_estoque');
  };

  return (
    <>
      {/* Modal Adicionar Estoque */}
      <div className="modal fade" id="modal_adicionar_estoque" tabIndex={-1} role="dialog" aria-hidden="true">
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h4 className="modal-title">Adicionar Estoque</h4>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
                onClick={handleCancel}
              >
                <i className="ti ti-x" />
              </button>
            </div>
            <div className="modal-body">
              {/* Informações do Produto */}
              <div className="alert alert-info mb-4">
                <div className="d-flex align-items-center">
                  <i className="ti ti-info-circle me-2"></i>
                  <div>
                    <strong>Produto:</strong> {produtoNome}<br />
                    <strong>Estoque Atual:</strong> {new Intl.NumberFormat('pt-BR').format(estoqueAtual)}
                  </div>
                </div>
              </div>

              <form onSubmit={handleSubmit}>
                <div className="row">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="col-form-label">
                        Quantidade <span className="text-danger">*</span>
                      </label>
                      <div className="input-group">
                        <NumericFormat
                          className="form-control"
                          value={formData.quantidade}
                          thousandSeparator="."
                          decimalSeparator=","
                          decimalScale={2}
                          fixedDecimalScale
                          allowNegative={false}
                          placeholder="0,00"
                          onValueChange={(values) => {
                            setFormData(prev => ({
                              ...prev,
                              quantidade: values.floatValue || 0
                            }));
                          }}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="col-form-label">
                        Motivo <span className="text-danger">*</span>
                      </label>
                      <select
                        className="form-select"
                        value={formData.motivo}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          motivo: e.target.value
                        }))}
                      >
                        <option value="">Selecione um motivo</option>
                        {motivosEstoque.map((motivo) => (
                          <option key={motivo} value={motivo}>
                            {motivo}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                <div className="mb-3">
                  <label className="col-form-label">Observações</label>
                  <textarea
                    className="form-control"
                    rows={3}
                    value={formData.observacoes}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      observacoes: e.target.value
                    }))}
                    placeholder="Observações adicionais (opcional)"
                  />
                </div>

                {/* Preview do novo estoque */}
                {formData.quantidade > 0 && (
                  <div className="alert alert-success">
                    <div className="d-flex align-items-center">
                      <i className="ti ti-check me-2"></i>
                      <div>
                        <strong>Novo estoque será:</strong> {new Intl.NumberFormat('pt-BR').format(estoqueAtual + formData.quantidade)}
                        <small className="d-block text-muted">
                          (+{new Intl.NumberFormat('pt-BR').format(formData.quantidade)})
                        </small>
                      </div>
                    </div>
                  </div>
                )}

                <div className="d-flex align-items-center justify-content-end mt-4">
                  <button
                    type="button"
                    className="btn btn-light me-2"
                    onClick={handleCancel}
                    disabled={isSubmitting}
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Adicionando...
                      </>
                    ) : (
                      <>
                        <i className="ti ti-plus me-2"></i>
                        Adicionar Estoque
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Modal de Sucesso */}
      <div className="modal fade custom-modal" id="success_estoque_modal" tabIndex={-1} role="dialog" aria-hidden="true">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-body">
              <div className="success-message text-center">
                <div className="avatar avatar-xl bg-success-light rounded-circle mb-3">
                  <i className="ti ti-check-check fs-36 text-success" />
                </div>
                <h3>Estoque Adicionado com Sucesso!</h3>
                <p>O estoque do produto foi atualizado com sucesso.</p>
                <div className="d-flex align-items-center justify-content-center mt-4">
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => modalUtils.fecharModal('success_estoque_modal')}
                  >
                    OK
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}; 